/**
 * Shared Project — Homeowner view (public, token-scoped).
 *
 * Route: /shared/:token[/schemes/:schemeId]
 *
 * Rules (Phase 5):
 *   - No login. Access is scoped by the URL token.
 *   - Only shows schemes in status `presented` or `approved`.
 *   - Option C · "token + soft claim": anyone with the link can READ, but
 *     commenting, requesting changes, or approving requires the homeowner
 *     to enter their name + email (cached in localStorage per token so
 *     they don't retype).
 *   - Every colour is joined live from the ColourMatch library — no stale
 *     hex ever renders (SSOT invariant).
 *   - Approvals are permanent; the "Approve" button is disabled once the
 *     scheme's status is `approved`.
 *
 * This page is intentionally NOT rendered inside <Layout> — the homeowner
 * sees a clean, chrome-free surface without the painter dashboard header.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Columns3,
  Download,
  Loader2,
  Lock,
  MessageCircle,
  Palette,
  Send,
  Sparkles,
  X,
  AlertTriangle,
} from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

// Dedicated axios instance — the homeowner route is deliberately unauthenticated
// so it does NOT go through the app-wide session-expiry 401 interceptor.
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API_BASE, withCredentials: false });

// Session-store key for "show paint store equivalents" toggle (Phase 6).
function _tradeToggleKey(token) { return `cm.shared.trade.${token}`; }
function loadTradeToggle(token) {
  try { return window.sessionStorage.getItem(_tradeToggleKey(token)) === "1"; }
  catch { return false; }
}
function saveTradeToggle(token, on) {
  try { window.sessionStorage.setItem(_tradeToggleKey(token), on ? "1" : "0"); }
  catch { /* ignore */ }
}

// LocalStorage key for the "soft claim" identity (see Option C).
function _claimKey(token) { return `cm.shared.claim.${token}`; }
function loadClaim(token) {
  try {
    const raw = window.localStorage.getItem(_claimKey(token));
    if (!raw) return null;
    const claim = JSON.parse(raw);
    if (claim && typeof claim.name === "string" && typeof claim.email === "string") return claim;
    return null;
  } catch { return null; }
}
function saveClaim(token, name, email) {
  try {
    window.localStorage.setItem(_claimKey(token), JSON.stringify({ name, email }));
  } catch { /* ignore quota */ }
}

// Simple utility: extract detail from an axios error.
function _err(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return e?.message || "Something went wrong.";
}

// ---------------------------------------------------------------------------
// Root page
// ---------------------------------------------------------------------------
export default function SharedProjectPage() {
  const { token, schemeId: routeSchemeId } = useParams();
  const [projectBundle, setProjectBundle] = useState(null); // {project, schemes[]}
  const [error, setError] = useState(null);
  const [selectedSchemeId, setSelectedSchemeId] = useState(routeSchemeId || null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [claim, setClaim] = useState(() => loadClaim(token));
  // Phase 6 — collapsed by default; per-session preference.
  const [showTrade, setShowTrade] = useState(() => loadTradeToggle(token));

  const loadBundle = useCallback(async () => {
    try {
      const { data } = await publicApi.get(`/pro/cms/shared/${token}`);
      setProjectBundle(data);
      // If the route didn't specify a scheme yet, default to the first
      // presented one so the homeowner lands on something meaningful.
      if (!selectedSchemeId && data.schemes?.length) {
        setSelectedSchemeId(
          data.project.approved_scheme_id ||
          data.schemes.find((s) => s.status === "presented")?.id ||
          data.schemes[0].id
        );
      }
    } catch (e) {
      setError(_err(e));
    }
     
  }, [token]);

  useEffect(() => { loadBundle(); }, [loadBundle]);

  // Keep the URL in sync with the selected scheme so the homeowner can share
  // deep-links back to us and refresh doesn't drop context.
  const nav = useNavigate();
  useEffect(() => {
    if (selectedSchemeId && selectedSchemeId !== routeSchemeId) {
      nav(`/shared/${token}/schemes/${selectedSchemeId}`, { replace: true });
    }
  }, [selectedSchemeId, routeSchemeId, token, nav]);

  if (error) return <ErrorScreen message={error} />;
  if (!projectBundle) return <FullPageLoader />;

  const { project, schemes } = projectBundle;

  return (
    <div className="min-h-screen bg-brand-linen/30" data-testid="shared-project-page">
      <Toaster position="top-right" richColors />
      <HomeownerHeader project={project} claim={claim} />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        <ProjectSummary
          project={project}
          schemes={schemes}
          claim={claim}
          onOpenCompare={() => setCompareOpen(true)}
          showTrade={showTrade}
          onToggleTrade={(on) => { saveTradeToggle(token, on); setShowTrade(on); }}
        />

        {schemes.length === 0 ? (
          <NoPresentedSchemesEmpty />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-6 mt-6">
            <SchemeRail
              schemes={schemes}
              selectedSchemeId={selectedSchemeId}
              onSelect={setSelectedSchemeId}
              approvedSchemeId={project.approved_scheme_id}
            />
            {selectedSchemeId ? (
              <SchemeView
                token={token}
                schemeId={selectedSchemeId}
                claim={claim}
                showTrade={showTrade}
                onClaimChange={(c) => {
                  saveClaim(token, c.name, c.email);
                  setClaim(c);
                }}
                onApproved={loadBundle}
                onChangesRequested={loadBundle}
              />
            ) : (
              <div className="text-sm text-brand-muted p-8 border border-brand-linen bg-white text-center">
                Pick a scheme from the list to see its details.
              </div>
            )}
          </div>
        )}
      </div>

      {compareOpen && (
        <HomeownerCompareDialog
          token={token}
          schemes={schemes}
          onClose={() => setCompareOpen(false)}
          onOpenScheme={(id) => { setSelectedSchemeId(id); setCompareOpen(false); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Screens
// ---------------------------------------------------------------------------
function ErrorScreen({ message }) {
  return (
    <div className="min-h-screen bg-brand-linen/40 flex items-center justify-center p-6" data-testid="shared-project-error">
      <div className="max-w-md bg-white border border-brand-linen p-8 text-center">
        <Lock className="w-8 h-8 mx-auto mb-4 text-brand-muted" />
        <h1 className="font-serif text-2xl text-brand-ink mb-2">Link unavailable</h1>
        <p className="text-sm text-brand-muted mb-6">{message}</p>
        <Link to="/" className="inline-flex items-center gap-1.5 text-brand-teal hover:underline text-sm">
          <ArrowLeft className="w-3.5 h-3.5" /> ColourMatch home
        </Link>
      </div>
    </div>
  );
}

function FullPageLoader() {
  return (
    <div className="min-h-screen bg-brand-linen/30 flex items-center justify-center" data-testid="shared-project-loader">
      <Loader2 className="w-6 h-6 animate-spin text-brand-muted" />
    </div>
  );
}

function NoPresentedSchemesEmpty() {
  return (
    <div className="mt-8 p-10 bg-white border border-brand-linen text-center" data-testid="shared-empty">
      <Sparkles className="w-6 h-6 mx-auto text-brand-muted mb-2" />
      <h2 className="font-serif text-xl text-brand-ink mb-1">Nothing to review yet</h2>
      <p className="text-sm text-brand-muted">
        Your painter hasn&apos;t presented any colour schemes for review yet. This page will update as soon as they do.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Header + summary
// ---------------------------------------------------------------------------
function HomeownerHeader({ project, claim }) {
  return (
    <header className="border-b border-brand-linen bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2 text-brand-ink">
          <Palette className="w-5 h-5 text-brand-teal" />
          <span className="font-serif text-lg tracking-tight">ColourMatch</span>
          <span className="hidden sm:inline text-[10px] uppercase tracking-wider text-brand-muted bg-brand-linen px-2 h-5 items-center inline-flex">
            Project review
          </span>
        </div>
        {claim && (
          <div className="text-xs text-brand-muted" data-testid="shared-claim-hint">
            Signed as <span className="text-brand-ink">{claim.name}</span>
          </div>
        )}
      </div>
    </header>
  );
}

function ProjectSummary({ project, schemes, claim, onOpenCompare, showTrade, onToggleTrade }) {
  const presentedCount = schemes.filter((s) => s.status === "presented" || s.status === "approved").length;
  const isApproved = !!project.approved_scheme_id;
  return (
    <section className="bg-white border border-brand-linen p-6" data-testid="shared-project-summary">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider text-brand-muted">Project review</div>
          <h1 className="font-serif text-3xl text-brand-ink mt-1" data-testid="shared-project-name">
            {project.name}
          </h1>
          {project.site_address && (
            <div className="text-sm text-brand-muted mt-1">{project.site_address}</div>
          )}
          {project.client_name && (
            <div className="text-xs text-brand-muted mt-0.5">For: {project.client_name}</div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isApproved && (
            <span
              className="inline-flex items-center gap-1 bg-brand-teal/10 text-brand-teal text-[10px] uppercase tracking-wide px-2 h-6"
              data-testid="shared-project-approved-badge"
            >
              <CheckCircle2 className="w-3 h-3" /> Approved
            </span>
          )}
          <button
            onClick={onOpenCompare}
            disabled={presentedCount < 2}
            data-testid="shared-compare-btn"
            className="inline-flex items-center gap-1.5 text-xs text-brand-ink border border-brand-linen bg-white px-3 h-8 hover:border-brand-teal disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={presentedCount < 2 ? "Need at least 2 schemes to compare" : "Compare schemes side-by-side"}
          >
            <Columns3 className="w-3.5 h-3.5" /> Compare
          </button>
        </div>
      </div>
      <p className="mt-4 text-sm text-brand-ink/70 max-w-3xl">
        Below are the colour scheme{presentedCount === 1 ? "" : "s"} your painter has presented for review.
        You can leave comments, request changes, or approve the one you love.
      </p>
      {/* Phase 6 — Paint store equivalents toggle. Collapsed by default. */}
      <div className="mt-4 pt-4 border-t border-brand-linen">
        <label className="inline-flex items-center gap-2 cursor-pointer text-xs text-brand-ink" data-testid="shared-trade-toggle-label">
          <input
            type="checkbox"
            checked={!!showTrade}
            onChange={(e) => onToggleTrade(e.target.checked)}
            data-testid="shared-trade-toggle"
            className="w-3.5 h-3.5 accent-brand-teal"
          />
          <span>Show paint store equivalents</span>
        </label>
        {showTrade && (
          <div className="text-[11px] text-brand-muted mt-1.5 max-w-2xl" data-testid="shared-trade-toggle-hint">
            These trade paint names are provided as purchasing references. Your approved ColourMatch colour remains the official project colour.
          </div>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Scheme rail (left column)
// ---------------------------------------------------------------------------
function SchemeRail({ schemes, selectedSchemeId, onSelect, approvedSchemeId }) {
  return (
    <aside className="lg:sticky lg:top-4 self-start" data-testid="shared-scheme-rail">
      <div className="text-[10px] uppercase tracking-wide text-brand-muted mb-2">Colour schemes</div>
      <ul className="space-y-1.5">
        {schemes.map((s) => {
          const on = s.id === selectedSchemeId;
          const isApproved = s.id === approvedSchemeId;
          return (
            <li key={s.id}>
              <button
                onClick={() => onSelect(s.id)}
                data-testid={`shared-scheme-pick-${s.id}`}
                className={`w-full text-left px-3 py-2.5 border transition-colors ${
                  on
                    ? "border-brand-teal bg-brand-teal/5"
                    : "border-brand-linen bg-white hover:border-brand-ink/50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm text-brand-ink font-medium flex-1 min-w-0 truncate">{s.name}</span>
                  {isApproved && <CheckCircle2 className="w-3.5 h-3.5 text-brand-teal shrink-0" />}
                </div>
                <div className="text-[10px] uppercase tracking-wide text-brand-muted mt-0.5">
                  {s.status}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Scheme detail (right column)
// ---------------------------------------------------------------------------
function SchemeView({ token, schemeId, claim, showTrade, onClaimChange, onApproved, onChangesRequested }) {
  const [scheme, setScheme] = useState(null);
  const [error, setError] = useState(null);
  const [claimDialog, setClaimDialog] = useState(null); // { intent, initialBody, surfaceSlug? }
  const [commentSurface, setCommentSurface] = useState(null); // slug being commented on
  const [downloadOpen, setDownloadOpen] = useState(false);

  const load = useCallback(async () => {
    setScheme(null);
    setError(null);
    try {
      const { data } = await publicApi.get(`/pro/cms/shared/${token}/schemes/${schemeId}`);
      setScheme(data);
    } catch (e) {
      setError(_err(e));
    }
  }, [token, schemeId]);

  useEffect(() => { load(); }, [load]);

  const withClaim = useCallback((intent, payload) => {
    // If we have a cached claim, use it directly. Otherwise open the
    // soft-claim dialog which will resume the action once the homeowner
    // supplies their name + email.
    if (claim && claim.name && claim.email) {
      return { commenter_name: claim.name, commenter_email: claim.email, ...payload };
    }
    setClaimDialog({ intent, payload });
    return null;
  }, [claim]);

  const submitComment = useCallback(async (surfaceSlug, body, claimObj) => {
    const activeClaim = claimObj || claim;
    if (!activeClaim) { setClaimDialog({ intent: "comment", payload: { surface_slug: surfaceSlug, body } }); return; }
    try {
      await publicApi.post(`/pro/cms/shared/${token}/schemes/${schemeId}/comments`, {
        commenter_name: activeClaim.name,
        commenter_email: activeClaim.email,
        surface_slug: surfaceSlug || null,
        body,
      });
      toast.success("Comment sent to your painter");
      setCommentSurface(null);
      await load();
    } catch (e) {
      toast.error(_err(e));
    }
  }, [token, schemeId, claim, load]);

  const submitDecision = useCallback(async (kind, body, claimObj) => {
    const activeClaim = claimObj || claim;
    if (!activeClaim) { setClaimDialog({ intent: kind, payload: { body } }); return; }
    try {
      const { data } = await publicApi.post(
        `/pro/cms/shared/${token}/schemes/${schemeId}/${kind === "approve" ? "approve" : "request-changes"}`,
        {
          commenter_name: activeClaim.name,
          commenter_email: activeClaim.email,
          body: body || null,
        },
      );
      if (kind === "approve") {
        toast.success("Approved — thanks!");
        onApproved?.();
      } else {
        toast.success("Change request sent to your painter");
        onChangesRequested?.();
      }
      await load();
      return data;
    } catch (e) {
      toast.error(_err(e));
    }
  }, [token, schemeId, claim, load, onApproved, onChangesRequested]);

  if (error) {
    return (
      <div className="p-8 border border-brand-linen bg-white text-center" data-testid="shared-scheme-error">
        <div className="text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 px-3 py-2 mb-3">
          {error}
        </div>
      </div>
    );
  }
  if (!scheme) {
    return (
      <div className="p-8 border border-brand-linen bg-white flex items-center gap-2 text-brand-muted">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading scheme…
      </div>
    );
  }

  const interior = scheme.surfaces.filter((s) => s.category === "interior");
  const exterior = scheme.surfaces.filter((s) => s.category === "exterior");
  const canDecide = scheme.status === "presented";

  return (
    <section className="space-y-6" data-testid="shared-scheme-view">
      {/* Header */}
      <div className="bg-white border border-brand-linen p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-brand-muted">{scheme.status}</div>
            <h2 className="font-serif text-2xl text-brand-ink mt-0.5" data-testid="shared-scheme-name">
              {scheme.name}
            </h2>
            {scheme.description && (
              <p className="text-sm text-brand-ink/80 mt-1.5 max-w-2xl">{scheme.description}</p>
            )}
            <div className="text-xs text-brand-muted mt-2">
              {scheme.assigned_surface_count} of {scheme.surface_total} surfaces assigned
            </div>
          </div>
          {scheme.status === "approved" ? (
            <span
              className="inline-flex items-center gap-1 bg-brand-teal/10 text-brand-teal text-[10px] uppercase tracking-wide px-2 h-6"
              data-testid="shared-scheme-approved-badge"
            >
              <CheckCircle2 className="w-3 h-3" /> Approved
            </span>
          ) : null}
        </div>

        {/* Painter notes (milestone_note / decisions) */}
        {scheme.notes?.length > 0 && (
          <div className="mt-4 border-t border-brand-linen pt-3" data-testid="shared-scheme-notes">
            <div className="text-[10px] uppercase tracking-wide text-brand-muted mb-2">Notes from your painter</div>
            <ul className="space-y-1.5">
              {scheme.notes.map((n) => (
                <li key={`${n.revision_number}-${n.event_type}`} className="text-xs text-brand-ink/80">
                  &ldquo;{n.note}&rdquo;
                  <span className="text-brand-muted ml-1.5">
                    · Rev {n.revision_number}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Decision bar */}
        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => setClaimDialog({ intent: "approve", payload: { body: "" } })}
            disabled={!canDecide}
            data-testid="shared-approve-btn"
            className="bg-brand-navy text-white hover:bg-brand-teal/90 disabled:opacity-50"
          >
            <CheckCircle2 className="w-4 h-4 mr-2" /> Approve this scheme
          </Button>
          <Button
            onClick={() => setClaimDialog({ intent: "request-changes", payload: { body: "" } })}
            disabled={!canDecide}
            variant="outline"
            data-testid="shared-request-changes-btn"
          >
            <Send className="w-4 h-4 mr-2" /> Request changes
          </Button>
          <Button
            onClick={() => setCommentSurface("__general__")}
            variant="outline"
            data-testid="shared-add-comment-btn"
          >
            <MessageCircle className="w-4 h-4 mr-2" /> Leave a comment
          </Button>
          {scheme.status === "approved" && (
            <Button
              onClick={() => setDownloadOpen(true)}
              variant="outline"
              data-testid="shared-download-report-btn"
              className="border-brand-teal/30 text-brand-teal hover:bg-brand-teal/10"
            >
              <Download className="w-4 h-4 mr-2" /> Download my colour report
            </Button>
          )}
        </div>
        {!canDecide && (
          <div className="mt-2 text-[11px] text-brand-muted">
            This scheme is {scheme.status}. Ask your painter to re-present it if you&apos;d like to change your decision.
          </div>
        )}
      </div>

      {/* Colour schedule */}
      <SurfaceGroup
        title="Interior"
        surfaces={interior}
        comments={scheme.comments}
        onComment={(slug) => setCommentSurface(slug)}
        showTrade={showTrade}
      />
      <SurfaceGroup
        title="Exterior"
        surfaces={exterior}
        comments={scheme.comments}
        onComment={(slug) => setCommentSurface(slug)}
        showTrade={showTrade}
      />

      {/* General (non-surface) comments */}
      <GeneralCommentsList comments={scheme.comments} />

      {/* Comment box */}
      {commentSurface && (
        <CommentDialog
          surfaceSlug={commentSurface === "__general__" ? null : commentSurface}
          surfaceLabel={
            commentSurface === "__general__"
              ? "General comment"
              : scheme.surfaces.find((s) => s.surface_slug === commentSurface)?.surface_display_name
          }
          onClose={() => setCommentSurface(null)}
          onSubmit={(body) => submitComment(commentSurface === "__general__" ? null : commentSurface, body, null)}
        />
      )}

      {/* Soft-claim dialog — collects name + email then resumes the action */}
      {claimDialog && (
        <SoftClaimDialog
          intent={claimDialog.intent}
          initialClaim={claim}
          onClose={() => setClaimDialog(null)}
          onConfirm={async (name, email, body) => {
            const nextClaim = { name, email };
            onClaimChange(nextClaim);
            setClaimDialog(null);
            if (claimDialog.intent === "comment") {
              await submitComment(claimDialog.payload.surface_slug, claimDialog.payload.body, nextClaim);
            } else if (claimDialog.intent === "approve") {
              await submitDecision("approve", body ?? claimDialog.payload.body, nextClaim);
            } else if (claimDialog.intent === "request-changes") {
              await submitDecision("request-changes", body ?? claimDialog.payload.body, nextClaim);
            }
          }}
        />
      )}

      {/* Homeowner download-report dialog (approved schemes only) */}
      <HomeownerDownloadDialog
        open={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        token={token}
        scheme={scheme}
      />
    </section>
  );
}

function SurfaceGroup({ title, surfaces, comments, onComment, showTrade }) {
  const commentsBySlug = useMemo(() => {
    const map = new Map();
    for (const c of comments || []) {
      if (c.surface_slug) {
        const arr = map.get(c.surface_slug) || [];
        arr.push(c);
        map.set(c.surface_slug, arr);
      }
    }
    return map;
  }, [comments]);

  return (
    <div className="bg-white border border-brand-linen" data-testid={`shared-surface-group-${title.toLowerCase()}`}>
      <div className="px-4 py-2.5 border-b border-brand-linen text-[10px] uppercase tracking-wide text-brand-muted font-medium">
        {title}
      </div>
      <ul className="divide-y divide-brand-linen">
        {surfaces.map((s) => (
          <SurfaceRow
            key={s.surface_slug}
            surface={s}
            comments={commentsBySlug.get(s.surface_slug) || []}
            onComment={() => onComment(s.surface_slug)}
            showTrade={showTrade}
          />
        ))}
      </ul>
    </div>
  );
}

function SurfaceRow({ surface, comments, onComment, showTrade }) {
  const [showComments, setShowComments] = useState(comments.length > 0);
  const [tradeOpen, setTradeOpen] = useState(false);
  const swatch = surface.assigned && !surface.colour_stale ? (
    <div
      className="w-12 h-12 border border-brand-linen shrink-0"
      style={{ background: surface.colour_hex }}
      title={`${surface.colour_name} · ${surface.colour_hex}`}
    />
  ) : surface.colour_stale ? (
    <div className="w-12 h-12 border-2 border-dashed border-yellow-400 bg-yellow-50 flex items-center justify-center shrink-0">
      <AlertTriangle className="w-4 h-4 text-yellow-800" />
    </div>
  ) : (
    <div className="w-12 h-12 border border-dashed border-brand-linen shrink-0" />
  );

  return (
    <li className="p-4" data-testid={`shared-surface-row-${surface.surface_slug}`}>
      <div className="flex items-center gap-4">
        {swatch}
        <div className="flex-1 min-w-0">
          <div className="text-sm text-brand-ink font-medium">{surface.surface_display_name}</div>
          {surface.assigned && !surface.colour_stale ? (
            <div className="text-xs text-brand-muted mt-0.5">
              {surface.colour_name}
              {surface.colour_family && <span className="mx-1.5">·</span>}
              {surface.colour_family && <span>{surface.colour_family}</span>}
              <span className="mx-1.5">·</span>
              <span className="font-mono">{surface.colour_hex}</span>
            </div>
          ) : surface.colour_stale ? (
            <div className="text-xs text-yellow-800 italic mt-0.5">Colour retired — ask your painter to reassign</div>
          ) : (
            <div className="text-xs text-brand-muted italic mt-0.5">Not set</div>
          )}
        </div>
        <div className="shrink-0 flex items-center gap-2">
          {showTrade && surface.assigned && !surface.colour_stale && (
            <button
              onClick={() => setTradeOpen(true)}
              data-testid={`shared-trade-btn-${surface.surface_slug}`}
              className="inline-flex items-center gap-1 text-[11px] text-brand-ink border border-brand-linen bg-white px-2 h-6 hover:border-brand-teal hover:text-brand-teal transition-colors"
              title="See equivalent paint store colours"
            >
              Trade
            </button>
          )}
          {comments.length > 0 && (
            <button
              onClick={() => setShowComments((s) => !s)}
              data-testid={`shared-toggle-comments-${surface.surface_slug}`}
              className="inline-flex items-center gap-1 text-[11px] text-brand-teal hover:underline"
            >
              <MessageCircle className="w-3 h-3" /> {comments.length}
            </button>
          )}
          <button
            onClick={onComment}
            data-testid={`shared-comment-btn-${surface.surface_slug}`}
            className="text-[11px] text-brand-muted hover:text-brand-teal transition-colors"
          >
            Comment
          </button>
        </div>
      </div>
      {showComments && comments.length > 0 && (
        <ul className="mt-3 ml-16 space-y-2" data-testid={`shared-comments-${surface.surface_slug}`}>
          {comments.map((c) => (
            <li key={c.id} className="text-xs text-brand-ink/80 border-l-2 border-brand-linen pl-3 py-1">
              <div className="text-brand-ink">{c.body}</div>
              <div className="text-[10px] text-brand-muted mt-0.5">
                {c.commenter_name} · {new Date(c.created_at).toLocaleString()}
                {c.resolved && <span className="ml-1.5 text-brand-teal">· resolved</span>}
              </div>
            </li>
          ))}
        </ul>
      )}
      {tradeOpen && (
        <SharedTradeEquivalentsDialog
          colourCode={surface.colour_code}
          colourName={surface.colour_name}
          colourHex={surface.colour_hex}
          onClose={() => setTradeOpen(false)}
        />
      )}
    </li>
  );
}

function GeneralCommentsList({ comments }) {
  const general = (comments || []).filter((c) => !c.surface_slug);
  if (general.length === 0) return null;
  return (
    <div className="bg-white border border-brand-linen" data-testid="shared-general-comments">
      <div className="px-4 py-2.5 border-b border-brand-linen text-[10px] uppercase tracking-wide text-brand-muted font-medium">
        General comments
      </div>
      <ul className="divide-y divide-brand-linen">
        {general.map((c) => (
          <li key={c.id} className="px-4 py-3">
            <div className="text-sm text-brand-ink">{c.body}</div>
            <div className="text-[10px] text-brand-muted mt-0.5">
              {c.commenter_name} · {new Date(c.created_at).toLocaleString()}
              {c.resolved && <span className="ml-1.5 text-brand-teal">· resolved</span>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dialogs
// ---------------------------------------------------------------------------
function CommentDialog({ surfaceLabel, onClose, onSubmit }) {
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    await onSubmit(body.trim());
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="shared-comment-dialog">
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-brand-ink">
              {surfaceLabel || "Leave a comment"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Leave a comment for your painter on this surface or scheme.
            </DialogDescription>
          </DialogHeader>
          <Label className="text-brand-ink mt-3 block">Your comment</Label>
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Anything you'd like to flag or ask?"
            rows={4}
            autoFocus
            className="mt-1.5"
            maxLength={4000}
            data-testid="shared-comment-body"
          />
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              type="submit"
              disabled={busy || !body.trim()}
              className="bg-brand-navy text-white hover:bg-brand-teal/90"
              data-testid="shared-comment-submit"
            >
              <Send className="w-4 h-4 mr-2" />
              {busy ? "Sending…" : "Send comment"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function SoftClaimDialog({ intent, initialClaim, onClose, onConfirm }) {
  const [name, setName] = useState(initialClaim?.name || "");
  const [email, setEmail] = useState(initialClaim?.email || "");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const needsBody = intent === "approve" || intent === "request-changes";

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setBusy(true);
    await onConfirm(name.trim(), email.trim().toLowerCase(), body.trim());
    setBusy(false);
  };

  const heading = intent === "approve"
    ? "Approve this scheme"
    : intent === "request-changes"
    ? "Request changes"
    : "Leave a comment";
  const cta = intent === "approve"
    ? "Approve"
    : intent === "request-changes"
    ? "Send request"
    : "Send";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid={`shared-claim-dialog-${intent}`}>
        <form onSubmit={submit}>
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-brand-ink">{heading}</DialogTitle>
            <DialogDescription className="text-xs text-brand-muted mt-1">
              We&apos;ll record your name and email against this decision so your painter has a full audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-brand-ink">Your name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Jane Smith"
                className="mt-1.5"
                autoFocus
                maxLength={140}
                data-testid="shared-claim-name"
              />
            </div>
            <div>
              <Label className="text-brand-ink">Your email</Label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jane@example.com"
                className="mt-1.5"
                maxLength={254}
                data-testid="shared-claim-email"
              />
            </div>
          </div>
          {needsBody && (
            <div className="mt-3">
              <Label className="text-brand-ink">
                {intent === "approve" ? "Optional note" : "What would you like changed?"}
              </Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                className="mt-1.5"
                maxLength={4000}
                placeholder={
                  intent === "approve"
                    ? "e.g. Looks perfect — please go ahead!"
                    : "e.g. Try a warmer white on the ceiling please."
                }
                data-testid="shared-claim-body"
              />
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button
              type="submit"
              disabled={busy || !name.trim() || !email.trim() || (intent === "request-changes" && !body.trim())}
              className="bg-brand-navy text-white hover:bg-brand-teal/90"
              data-testid="shared-claim-submit"
            >
              {intent === "approve" && <CheckCircle2 className="w-4 h-4 mr-2" />}
              {intent === "request-changes" && <Send className="w-4 h-4 mr-2" />}
              {intent === "comment" && <MessageCircle className="w-4 h-4 mr-2" />}
              {busy ? "Sending…" : cta}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Homeowner compare dialog (public — same shape as painter Compare)
// ---------------------------------------------------------------------------
function HomeownerCompareDialog({ token, schemes, onClose, onOpenScheme }) {
  const [picked, setPicked] = useState(() => schemes.slice(0, Math.min(schemes.length, 3)).map((s) => s.id));
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (id) => {
    setPicked((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 4) return cur;
      return [...cur, id];
    });
  };

  const load = useCallback(async () => {
    if (picked.length < 2) { setMatrix(null); return; }
    setLoading(true);
    setError(null);
    try {
      const { data } = await publicApi.get(`/pro/cms/shared/${token}/compare`, {
        params: { scheme_ids: picked.join(",") },
      });
      setMatrix(data);
    } catch (e) {
      setError(_err(e));
      setMatrix(null);
    } finally {
      setLoading(false);
    }
  }, [token, picked]);

  useEffect(() => { load(); }, [load]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="shared-compare-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-brand-ink">Compare schemes</DialogTitle>
          <DialogDescription className="sr-only">
            Compare two or more of the schemes your painter has presented, side by side.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 mb-4 mt-3" data-testid="shared-compare-picker">
          {schemes.map((s) => {
            const on = picked.includes(s.id);
            const disabled = !on && picked.length >= 4;
            return (
              <button
                key={s.id}
                onClick={() => toggle(s.id)}
                disabled={disabled}
                data-testid={`shared-compare-pick-${s.id}`}
                className={`px-3 h-8 text-xs border transition-colors ${
                  on
                    ? "border-brand-teal bg-brand-teal/10 text-brand-teal"
                    : disabled
                    ? "border-brand-linen bg-brand-linen/20 text-brand-muted cursor-not-allowed"
                    : "border-brand-linen bg-white text-brand-ink hover:border-brand-ink"
                }`}
              >
                {on && <CheckCircle2 className="w-3 h-3 mr-1 inline" />}
                {s.name}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-auto border border-brand-linen bg-white">
          {picked.length < 2 ? (
            <div className="p-8 text-sm text-brand-muted text-center">Pick at least 2 schemes to compare.</div>
          ) : loading ? (
            <div className="p-8 text-sm text-brand-muted flex items-center gap-2 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading comparison…
            </div>
          ) : error ? (
            <div className="p-8 text-sm text-yellow-800 bg-yellow-50 text-center">{error}</div>
          ) : matrix ? (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-brand-linen/40 z-10">
                <tr>
                  <th className="text-left px-3 py-2 font-medium text-brand-muted uppercase tracking-wide text-[10px]">Surface</th>
                  {matrix.schemes.map((s) => (
                    <th key={s.id} className="text-left px-3 py-2">
                      <button
                        onClick={() => onOpenScheme(s.id)}
                        className="font-medium text-brand-ink hover:text-brand-teal text-left"
                      >
                        {s.name}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {["interior", "exterior"].flatMap((cat) => {
                  const catRows = matrix.rows.filter((r) => r.category === cat);
                  return [
                    <tr key={`cat-${cat}`}>
                      <td colSpan={matrix.schemes.length + 1} className="bg-brand-linen/20 px-3 py-1.5 text-[10px] uppercase tracking-wide text-brand-muted font-medium">
                        {cat}
                      </td>
                    </tr>,
                    ...catRows.map((row) => (
                      <tr key={row.surface_slug} className="border-t border-brand-linen">
                        <td className="px-3 py-2 text-brand-ink font-medium w-40">
                          {row.surface_display_name}
                        </td>
                        {row.cells.map((cell, i) => (
                          <td key={`${row.surface_slug}-${matrix.schemes[i].id}`} className="px-3 py-2 align-top">
                            {cell.assigned && !cell.colour_stale ? (
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-6 h-6 border border-brand-linen shrink-0" style={{ background: cell.colour_hex }} />
                                <div className="min-w-0">
                                  <div className="text-brand-ink truncate">{cell.colour_name}</div>
                                  <div className="text-[10px] text-brand-muted font-mono">{cell.colour_hex}</div>
                                </div>
                              </div>
                            ) : (
                              <span className="text-brand-muted italic">
                                {cell.colour_stale ? "Colour retired" : "Not set"}
                              </span>
                            )}
                          </td>
                        ))}
                      </tr>
                    )),
                  ];
                })}
              </tbody>
            </table>
          ) : null}
        </div>

        <DialogFooter className="mt-3">
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Phase 6 · Homeowner-facing trade equivalents dialog (read-only)
// ---------------------------------------------------------------------------
// The homeowner endpoint that returns this data is NOT the painter-only
// `/pro/cms/library-code/{code}/equivalents` — instead we reuse the
// existing public `/paint-aliases/search` endpoint by resolving the
// ColourMatch code back to its brand aliases via the alias search. But
// simpler: mount a public read-only endpoint on the same shared token.
//
// For v1 we lean on the painter-side reverse-lookup endpoint via the
// admin-open `/paint-aliases/search` — which is public — filtered by
// `library_code`. If we later see abuse we can gate this behind the
// share token like other homeowner writes.
function SharedTradeEquivalentsDialog({ colourCode, colourName, colourHex, onClose }) {
  const [state, setState] = useState({ loading: true, brands: null, error: null });
  useEffect(() => {
    (async () => {
      try {
        // Search over all aliases and filter to this library_code. The
        // /paint-aliases/search endpoint requires a query; use the library
        // colour name as the seed (most aliases share tokens with the
        // library name). Fallback: broad search on "a" and filter locally.
        const seeds = [colourName || "", (colourName || "").split(" ")[0], "a"];
        let items = [];
        for (const seed of seeds) {
          if (!seed || seed.length < 1) continue;
          try {
            const { data } = await publicApi.get("/paint-aliases/search", {
              params: { q: seed, limit: 25 },
            });
            const rows = (data.results || []).filter((r) => r.library_code === colourCode);
            if (rows.length) { items = rows; break; }
          } catch { /* try next */ }
        }
        // Group by brand.
        const by = new Map();
        for (const r of items) {
          const b = (r.brand || "Unknown").trim() || "Unknown";
          const arr = by.get(b) || [];
          arr.push(r);
          by.set(b, arr);
        }
        const brands = [...by.entries()]
          .sort(([a], [b]) => a.toLowerCase().localeCompare(b.toLowerCase()))
          .map(([brand, list]) => ({ brand, items: list }));
        setState({ loading: false, brands, error: null });
      } catch (e) {
        setState({ loading: false, brands: null, error: _err(e) });
      }
    })();
  }, [colourCode, colourName]);

  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { /* ignore */ }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col" data-testid="shared-trade-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-brand-ink">Paint store equivalents</DialogTitle>
          <DialogDescription className="text-xs text-brand-muted mt-1">
            These trade paint names are provided as purchasing references. Your approved ColourMatch colour remains the official project colour.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-3 flex items-center gap-3 border border-brand-linen bg-brand-linen/20 p-3">
          <div className="w-10 h-10 border border-brand-linen shrink-0" style={{ background: colourHex }} />
          <div className="min-w-0">
            <div className="text-sm text-brand-ink font-medium">{colourName}</div>
            <div className="text-[11px] text-brand-muted font-mono">{colourHex}</div>
          </div>
        </div>
        <div className="flex-1 overflow-auto border border-brand-linen bg-white mt-3">
          {state.loading ? (
            <div className="p-4 text-sm text-brand-muted flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : state.error ? (
            <div className="p-4 text-sm text-yellow-800 bg-yellow-50">{state.error}</div>
          ) : !state.brands || state.brands.length === 0 ? (
            <div className="p-4 text-sm text-brand-muted text-center">
              No paint store equivalents recorded yet for this colour.
            </div>
          ) : (
            <ul className="divide-y divide-brand-linen">
              {state.brands.map((b) => (
                <li key={b.brand} className="p-3" data-testid={`shared-trade-brand-${b.brand}`}>
                  <div className="text-[10px] uppercase tracking-wide text-brand-muted font-medium mb-1.5">
                    {b.brand} · {b.items.length}
                  </div>
                  <ul className="space-y-1">
                    {b.items.map((it, i) => (
                      <li key={`${b.brand}-${it.trade_name}-${i}`} className="flex items-center gap-2 text-xs">
                        <div className="w-4 h-4 border border-brand-linen shrink-0" style={{ background: it.ref_hex }} />
                        <span className="text-brand-ink flex-1 min-w-0 truncate">{it.trade_name}</span>
                        <span className="font-mono text-brand-muted">{it.ref_hex}</span>
                        <button
                          onClick={() => copy(`${b.brand} ${it.trade_name}`)}
                          className="text-brand-muted hover:text-brand-teal text-[10px] uppercase tracking-wide"
                        >
                          Copy
                        </button>
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
        </div>
        <DialogFooter className="mt-3">
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ---------------------------------------------------------------------------
// Homeowner download-report dialog (Phase 7)
// ---------------------------------------------------------------------------
function HomeownerDownloadDialog({ open, onClose, token, scheme }) {
  const [includeTrade, setIncludeTrade] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open || !scheme) return null;

  async function handleDownload() {
    setBusy(true);
    try {
      const res = await publicApi.post(
        `/pro/cms/shared/${token}/exports/colour-schedule`,
        { include_trade: includeTrade },
        { responseType: "blob" },
      );
      const disp = res.headers?.["content-disposition"] || "";
      const m = disp.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `colour-report.pdf`;
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Colour report downloaded.");
      onClose?.();
    } catch (err) {
      let detail = "Download failed.";
      try {
        if (err.response?.data && err.response.data instanceof Blob) {
          const txt = await err.response.data.text();
          try { detail = JSON.parse(txt).detail || detail; }
          catch { detail = txt || detail; }
        } else {
          detail = err.response?.data?.detail || err.message || detail;
        }
      } catch { /* fall through */ }
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent data-testid="shared-download-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-xl text-brand-ink">Download my colour report</DialogTitle>
          <DialogDescription>
            A print-ready PDF of the approved scheme <span className="text-brand-ink">{scheme.name}</span>,
            branded by your painter. The approved ColourMatch library colour is always the official project colour.
          </DialogDescription>
        </DialogHeader>
        <label
          className="flex items-start gap-3 border border-brand-linen p-3 cursor-pointer hover:border-brand-teal transition-colors"
          data-testid="shared-download-trade-toggle-label"
        >
          <input
            type="checkbox"
            className="mt-0.5"
            checked={includeTrade}
            onChange={(e) => setIncludeTrade(e.target.checked)}
            data-testid="shared-download-trade-toggle"
          />
          <div>
            <div className="text-sm text-brand-ink font-medium">Include trade equivalents</div>
            <div className="text-xs text-brand-muted mt-0.5">
              Appends a purchasing reference table (Dulux / Taubmans / Resene …). Purchasing reference only —
              your approved ColourMatch colour remains the official project colour.
            </div>
          </div>
        </label>
        <DialogFooter className="mt-3">
          <Button type="button" variant="outline" onClick={onClose} data-testid="shared-download-cancel">
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleDownload}
            disabled={busy}
            data-testid="shared-download-confirm"
            className="bg-brand-navy text-white hover:bg-brand-teal/90"
          >
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
